import React, { useState, useEffect } from 'react';
import { Volume2, Play, Pause, Download } from 'lucide-react';
import './ToolsCommon.css';

const TextToSpeech = () => {
  const [text, setText] = useState('');
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);
      if (availableVoices.length > 0 && !selectedVoice) {
        setSelectedVoice(availableVoices[0]);
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  const speak = () => {
    if (!text) return;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = selectedVoice;
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = volume;

    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);

    window.speechSynthesis.speak(utterance);
  };

  const stop = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
  };

  const downloadAudio = () => {
    // Note: Web Speech API doesn't support direct audio download
    // This would require a backend service to convert TTS to audio file
    alert('Audio download feature requires a backend service. The browser Text-to-Speech API doesn\'t support direct audio file generation. You can use the play button to listen to the text.');
  };

  return (
    <div className="tool-container">
      <div className="tool-content-wrapper">
        <div className="tool-header">
          <Volume2 size={48} className="tool-icon" />
          <h1 className="tool-title">Text to Speech</h1>
          <p className="tool-subtitle">Convert text to natural speech</p>
        </div>

        <div className="tool-panel">
          <div className="tool-section">
            <label className="tool-label">Enter Text</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type or paste your text here..."
              className="tool-textarea"
              rows="8"
            />
          </div>

          <div className="tool-grid">
            <div className="tool-section">
              <label className="tool-label">Voice</label>
              <select
                value={selectedVoice?.name || ''}
                onChange={(e) => {
                  const voice = voices.find(v => v.name === e.target.value);
                  setSelectedVoice(voice);
                }}
                className="tool-input"
                style={{ cursor: 'pointer' }}
              >
                {voices.map((voice) => (
                  <option key={voice.name} value={voice.name}>
                    {voice.name} ({voice.lang})
                  </option>
                ))}
              </select>
            </div>

            <div className="tool-section">
              <label className="tool-label">Speed: {rate.toFixed(1)}x</label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="tool-range"
              />
            </div>

            <div className="tool-section">
              <label className="tool-label">Pitch: {pitch.toFixed(1)}</label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={pitch}
                onChange={(e) => setPitch(Number(e.target.value))}
                className="tool-range"
              />
            </div>

            <div className="tool-section">
              <label className="tool-label">Volume: {Math.round(volume * 100)}%</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="tool-range"
              />
            </div>
          </div>

          <div className="tool-actions">
            {!isPlaying ? (
              <button onClick={speak} disabled={!text} className="tool-button primary">
                <Play size={20} />
                Play Speech
              </button>
            ) : (
              <button onClick={stop} className="tool-button secondary">
                <Pause size={20} />
                Stop
              </button>
            )}
            <button onClick={downloadAudio} disabled={!text} className="tool-button secondary">
              <Download size={20} />
              Download Audio (Info)
            </button>
          </div>

          <div style={{ marginTop: '24px', padding: '16px', background: '#f9fafb', borderRadius: '12px', fontSize: '14px', color: '#6b7280' }}>
            <strong>Tip:</strong> This tool uses your browser's built-in text-to-speech engine. Available voices and quality may vary depending on your operating system and browser.
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextToSpeech;
